import type { RootProps, TFieldMapping, ISettings } from './types'
import { getCurrentInstance } from 'vue'
import type { IPricing } from './types'

export function getAccountId(): string {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const accountIdFromUrl = urlParams.get('account_id')

  if (accountIdFromUrl && accountIdFromUrl !== '') {
    return accountIdFromUrl
  }

  // Check DOM attributes
  const app = getCurrentInstance()
  const rootProps = app?.proxy?.$root?.$attrs as RootProps | undefined
  const accountIdFromElement = rootProps?.accountId ?? ''

  if (accountIdFromElement && accountIdFromElement !== '') {
    return accountIdFromElement
  }

  return ''
}

export function getPlacementId(): string {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const placementIdFromUrl = urlParams.get('placement_id')

  if (placementIdFromUrl && placementIdFromUrl !== '') {
    return placementIdFromUrl
  }

  // Check DOM attributes
  const app = getCurrentInstance()
  const rootProps = app?.proxy?.$root?.$attrs as RootProps | undefined
  const placementIdFromElement = rootProps?.placementId ?? 'default'

  if (placementIdFromElement && placementIdFromElement !== '') {
    return placementIdFromElement
  }

  return ''
}

/**
 * Evaluates a conditional expression within a template
 * @param expression The expression to evaluate
 * @param data The data object containing values
 * @param fieldMapping
 * @returns The result of the evaluated expression
 */
const evaluateCondition = (
  expression: string,
  data: { [key: string]: any },
  fieldMapping: TFieldMapping = getDefaultMapping(),
): string => {
  const mapping = fieldMapping ?? getDefaultMapping()

  // Handle the most nested template expressions first
  const processedExp = expression.replace(
    /\{\{([^{}]+)\}\}/g,
    (match, field) => {
      const trimmedField = field.trim()

      // If it's a comparison expression, return as is
      if (field.includes('==') || field.includes('!=')) return field
      if (field.startsWith('"') && field.endsWith('"')) return field

      // Handle field mapping
      let mappedField = mapping[trimmedField] || trimmedField

      if (trimmedField.startsWith('@')) {
        mappedField = 'pricing'
      }

      const fieldValue = getNestedValue(data, mappedField, undefined)

      if (fieldValue !== undefined) {
        const value = parseFieldValue(trimmedField, fieldValue)

        if (Array.isArray(value)) {
          return `"${value.join(',')}"`
        }

        // Coerce basic primitives to JS literals so conditionals behave intuitively
        const coerceToJsLiteral = (val: any): string => {
          if (typeof val === 'boolean') return String(val)
          if (typeof val === 'number') return String(val)
          if (typeof val === 'string') {
            const normalized = val.trim().toLowerCase()
            if (normalized === 'true' || normalized === '1') return 'true'
            if (normalized === 'false' || normalized === '0') return 'false'
            const asNum = Number(val)
            if (!Number.isNaN(asNum)) return String(asNum)
            return JSON.stringify(val)
          }
          return JSON.stringify(val)
        }

        return coerceToJsLiteral(value)
      }

      return JSON.stringify(field)
    },
  )

  // Simple field reference (no condition)
  const simpleField = expression.match(/^\{\{([^{}]+)\}\}$/)
  if (simpleField) {
    const field = simpleField[1].trim()
    const mappedField = mapping[field] || field

    const fieldValue = getNestedValue(data, mappedField, undefined)

    if (fieldValue !== undefined) {
      return formatFieldValue(field, fieldValue)
    }
    return expression
  }

  // Now handle the conditional expression
  const conditionalMatch = processedExp.match(
    /\((.*?)\)\s*\?\s*"(.*?)"\s*:\s*"(.*?)"$/,
  )
  if (!conditionalMatch) return expression

  const [_, condition, trueOutcome, falseOutcome] = conditionalMatch

  try {
    // Evaluate the condition - handle both single conditions and OR conditions
    const evalCondition = condition.replace(
      /([^"!=\s]+)\s*(!=|==)\s*("([^"]*)"|(\d+))/g,
      (match, field, operator, quotedValue, numericValue) => {
        const fieldMapping = getDefaultMapping()
        const mappedField = fieldMapping[field] ?? field
        const fieldValue = getNestedValue(data, mappedField, undefined)

        if (fieldValue !== undefined) {
          if (Array.isArray(fieldValue)) {
            return `"${fieldValue.join(',')}" ${operator} ${quotedValue || numericValue}`
          }
          return `"${fieldValue}" ${operator} ${quotedValue || numericValue}`
        }

        return `undefined ${operator} ${quotedValue || numericValue}`
      },
    )

    const result = eval(evalCondition)

    const processOutcome = (outcome: string) => {
      // Handle empty string case
      if (outcome === '') return ''

      // Process field references within text
      const processed = outcome.replace(/\{\{([^{}]+)\}\}/g, (match, field) => {
        const trimmedField = field.trim()
        const mappedField = mapping[trimmedField] || trimmedField
        const value = getNestedValue(data, mappedField, undefined)

        if (value !== undefined) {
          if (Array.isArray(value)) {
            return value.join(',').trim()
          }
          // Remove any quotes from the formatted value
          return formatFieldValue(trimmedField, value)
            .toString()
            .replace(/^"|"$/g, '')
            .trim()
        }
        return match
      })

      // Remove quotes from the beginning and end of the entire outcome
      // Also handle cases where there might be extra quotes
      let finalProcessed = processed.replace(/^"|"$/g, '')

      // If the outcome still has quotes, try to remove them more aggressively
      if (finalProcessed.startsWith('"') && finalProcessed.endsWith('"')) {
        finalProcessed = finalProcessed.slice(1, -1)
      }

      return finalProcessed
    }

    const finalOutcome = result
      ? processOutcome(trueOutcome)
      : processOutcome(falseOutcome)
    // Return empty string if it was an empty string in the template
    if (finalOutcome === '""') return ''
    return finalOutcome
  } catch (e) {
    console.error('Error evaluating condition:', e)
    return expression
  }
}

/**
 * Gets a nested property value from an object using dot notation
 * @param obj The object to retrieve the value from
 * @param path The dot-separated path to the property
 * @param defaultValue Optional default value to return if the property doesn't exist
 * @returns The value at the specified path or the default value
 */
function getNestedValue<T = any, D = undefined>(
  obj: Record<string, any>,
  path: string,
  defaultValue?: D,
): T | D {
  if (!obj || !path) {
    return defaultValue as D
  }

  const keys = path.split('.')
  let current = obj

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return defaultValue as D
    }

    current = current[key]
  }

  return current !== undefined ? (current as T) : (defaultValue as D)
}

const parseFieldValue = (key: string, value: any) => {
  const parsePricingField = (key: string, value: any) => {
    const pricingKey = key === 'msrp' ? '@MSRP' : key
    const pricingData: IPricing = value
    const pricingItem = pricingData?.find(
      (item: any) => item.pricing.Name === pricingKey,
    )

    if (!pricingItem) return ''

    return pricingItem.pricing.Value
  }

  if (key === 'msrp' || key.startsWith('@')) {
    if (value == null || value === '') {
      return ''
    }

    const result = parsePricingField(key, value)
    // Ensure we return empty string instead of undefined
    return result || ''
  }

  return value
}

/**
 * Processes a template string with provided data
 * @param template The template string to process
 * @param data The vehicle data item
 * @param fieldMapping
 * @returns Processed string with replaced variables
 */
export const processTemplate = (
  template: string,
  data: { [key: string]: any },
  fieldMapping: TFieldMapping = getDefaultMapping(),
): string => {
  // Add template validation
  if (!template || typeof template !== 'string') {
    console.warn('Invalid template provided:', template)
    return ''
  }

  let result = template
  let previousResult
  do {
    previousResult = result
    result = result.replace(
      /\{\{(\(\{\{[^}]+\}\}\)\s*\?\s*"[^"]*"\s*:\s*"[^"]*")\}\}/g,
      (match, expression) => {
        return evaluateCondition(expression, data, fieldMapping)
      },
    )
  } while (result !== previousResult)

  result = result.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const trimmedKey = key.trim()

    // Handle conditional fields with format: field|conditional|prefix|suffix|hideValue
    if (trimmedKey.includes('|conditional|')) {
      const parts = trimmedKey.split('|conditional|')
      if (parts.length !== 2) {
        console.warn('Malformed conditional template:', trimmedKey)
        return match // Return original if malformed
      }

      const [fieldName, conditionalPart] = parts
      const conditionalParts = conditionalPart.split('|')

      // Ensure we have the expected number of parts
      if (conditionalParts.length !== 3) {
        console.warn('Invalid conditional parts:', conditionalParts)
        return match // Return original if malformed
      }

      const [prefix, suffix, hideValue] = conditionalParts

      let mappedKey = fieldMapping[fieldName] || fieldName
      if (fieldName.startsWith('@') || fieldName === 'custom') {
        mappedKey = 'pricing'
      }

      const fieldValue = getNestedValue(data, mappedKey, undefined)
      const value = parseFieldValue(fieldName, fieldValue)

      // Treat empty, zero, and negative values as "no value" for pricing fields
      const isPricingField =
        fieldName === 'price' || fieldName === 'msrp' || fieldName.startsWith('@')

      // First, handle general empties and explicit zero strings/numbers
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        value === 0 ||
        value === '0'
      ) {
        return hideValue || ''
      }

      // For pricing fields, also hide negative numeric values
      if (isPricingField) {
        let numeric: number | null = null
        if (typeof value === 'number') {
          numeric = value
        } else if (typeof value === 'string') {
          const normalized = value.replace(/[^0-9.-]/g, '')
          const parsed = Number(normalized)
          numeric = Number.isNaN(parsed) ? null : parsed
        }

        if (numeric !== null && numeric <= 0) {
          return hideValue || ''
        }
      }

      // Value exists, format it with prefix and suffix
      const formattedValue = formatFieldValue(fieldName, value)

      // Simply concatenate prefix + value + suffix without automatic spacing
      return `${prefix || ''}${formattedValue}${suffix || ''}`
    }

    // Handle regular fields
    let mappedKey = fieldMapping[trimmedKey] || trimmedKey

    if (trimmedKey.startsWith('@')) {
      mappedKey = 'pricing'
    }

    // Try primary mapping
    let fieldValue = getNestedValue(data, mappedKey, undefined)

    // Fallback aliases for common color field variants
    if (fieldValue === undefined) {
      const lower = trimmedKey.toLowerCase()
      const aliasMap: Record<string, string[]> = {
        interiorcolor: [
          'visual.colors.interior.factory',
          'visual.colors.interior.generic',
          'colors.interior.factory',
          'colors.interior.generic',
          'interior_factory_color',
          'interior_color',
        ],
        exteriorcolor: [
          'visual.colors.exterior.factory',
          'visual.colors.exterior.generic',
          'colors.exterior.factory',
          'colors.exterior.generic',
          'exterior_factory_color',
          'exterior_color',
        ],
      }
      const aliases = aliasMap[lower]
      if (aliases && aliases.length) {
        for (const alt of aliases) {
          const altVal = getNestedValue(data, alt, undefined)
          if (altVal !== undefined && altVal !== null && altVal !== '') {
            fieldValue = altVal
            break
          }
        }
      }
    }

    const value = parseFieldValue(trimmedKey, fieldValue)

    if (Array.isArray(value)) {
      return value.join(', ')
    }

    return formatFieldValue(key, value)
  })

  return result
}

/**
 * Processes multiple templates for a vehicle item
 * @param templates Array of template strings
 * @param item
 * @param fieldMapping
 * @returns Array of processed strings
 */
export const processTemplates = (
  templates: string[],
  item: { fields: { [key: string]: any } },
  fieldMapping: TFieldMapping = getDefaultMapping(),
): string[] => {
  // Access the field property from the item object
  return templates.map(template =>
    processTemplate(template, item.fields, fieldMapping),
  )
}

export const getDefaultMapping = (): TFieldMapping => {
  return {
    address: 'location_address1',
    city: 'location_city',
    condition: 'condition',
    conditions: 'conditions',
    discount: 'discount',
    // Color fields (support multiple casings commonly used by admin templates)
    // Prefer nested colors object from API
    interiorColor: 'colors.interior.factory',
    InteriorColor: 'colors.interior.factory',
    exteriorColor: 'colors.exterior.factory',
    ExteriorColor: 'colors.exterior.factory',
    phoneNumber: 'location_contact_number',
    price: 'displayPrice',
    msrp: 'pricing',
    state: 'location.state',
    stockNumber: 'stockNumber',
    style: 'visual.styleName',
    zipCode: 'location.zipCode',
  }
}

export const validateSettings = (settings: ISettings): boolean => {
  return settings.hasOwnProperty('placement')
}

export const formatFieldValue = (key: string, value: any): string => {
  // Return empty string for undefined, null, or empty values
  if (value === undefined || value === null || value === '') {
    return ''
  }

  if (key.startsWith('@') || key === 'custom') {
    return formatPrice(value)
  }

  switch (key) {
    case 'price':
    case 'discount':
    case 'msrp':
      return formatPrice(value)
    case 'mileage':
      return formatNumberWithUnit(value, 'mi')
    case 'conditions':
      return Array.isArray(value) ? value.join(', ') : value
    default:
      return value
  }
}

export const formatPrice = (
  value: string | number = 0,
  options = { showCents: false, showSymbol: true },
): string => {
  const { showCents = true, showSymbol = true } = options

  const valueAsNumber = Number(value)

  // Handle invalid inputs
  if (typeof value === 'string' && isNaN(valueAsNumber)) {
    return value
  }

  // Round to 2 decimal places
  const roundedAmount = Math.round(valueAsNumber * 100) / 100

  // Format with proper grouping and decimal places
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
    currencyDisplay: showSymbol ? 'symbol' : 'code',
  })

  // Remove 'USD' if not showing symbol
  let formatted = formatter.format(roundedAmount)
  if (!showSymbol) {
    formatted = formatted.replace('USD', '').trim()
  }

  return formatted
}

export const formatNumberWithUnit = (
  value: string | number,
  unit: string = '',
  options = {
    decimals: 0,
    spaceBeforeUnit: true,
    unitBefore: false,
    locale: 'en-US',
  },
) => {
  const {
    decimals = 0,
    spaceBeforeUnit = true,
    unitBefore = false,
    locale = 'en-US',
  } = options
  const valueAsNumber = Number(value)

  // Handle invalid inputs
  if (typeof value === 'string' && isNaN(valueAsNumber)) {
    return 'Invalid number'
  }

  // Format number with proper grouping and decimal places
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  const formattedNumber = formatter.format(valueAsNumber)

  // Handle unit placement
  if (!unit) return formattedNumber

  if (unitBefore) {
    return `${unit}${spaceBeforeUnit ? ' ' : ''}${formattedNumber}`
  }

  return `${formattedNumber}${spaceBeforeUnit ? ' ' : ''}${unit}`
}
